import React, { useMemo, useState } from "react";
import { Modal, Tag, Empty, Spin, Descriptions, Divider, Select } from "antd";
import { UserOutlined, ArrowDownOutlined, EditOutlined, AppstoreOutlined, DownOutlined, RightOutlined } from "@ant-design/icons";
import moment from "moment";
import { getFormalName } from "components/services";
import { STAGES } from "components/common/Constants";

const ActivityLogModal = ({ open, onCancel, jobcardId, jobcardSerialNumber, activityData, loading }) => {
	const [expandedItems, setExpandedItems] = useState({});
	const [viewType, setViewType] = useState("timewise");
	// Define stages in order
	const STAGES_ORDER = [
		STAGES.CREATION,
		STAGES.CUTTING,
		STAGES.SECONDARY_PROCESS,
		STAGES.EMBROIDARY,
		STAGES.PRODUCTION,
		STAGES.PACKING,
		STAGES.ACCOUNT,
		STAGES.PAYMENT,
		STAGES.DISPATCH,
		STAGES.COMPLETED,
	];

	// Group activities by stage
	const activitiesByStage = useMemo(() => {
		if (!activityData || !activityData.activities) return {};

		const grouped = {};
		
		// Initialize all stages
		STAGES_ORDER.forEach((stage) => {
			grouped[stage] = [];
		});

		// Group activities by stage_name
		activityData.activities.forEach((activity) => {
			// Skip digitizer activities - they will be handled separately
			if (activity.activity_type === "digitizer_entry" || activity.activity_type === "digitizer_exit") {
				return; // Skip digitizer activities in stage grouping
			}
			
			// Filter out all is_degitizer_stage related activities
			if (activity.activity_type === "field_update" && activity.field_name === "is_degitizer_stage") {
				return; // Skip this activity completely
			}
			
			// Filter out is_degitizer_stage from bulk_field_update changes
			if (activity.activity_type === "bulk_field_update" && activity.changes) {
				const hasOnlyDigitizerStage = activity.changes.length === 1 && 
					activity.changes[0].field === "is_degitizer_stage";
				if (hasOnlyDigitizerStage) {
					return; // Skip this activity completely
				}
			}

			// Handle creation activities - they go to CREATION stage
			if (activity.activity_type === "created") {
				const targetStage = activity.stage_name || STAGES.CREATION;
				if (!grouped[targetStage]) {
					grouped[targetStage] = [];
				}
				grouped[targetStage].push(activity);
			}
			// Handle stage_change - show in previous stage (as "forwarded")
			else if (activity.activity_type === "stage_change" && activity.previous_stage) {
				const previousStage = activity.previous_stage;
				if (!grouped[previousStage]) {
					grouped[previousStage] = [];
				}
				grouped[previousStage].push(activity);
			}
			// Handle stage_entry and stage_exit - show in their respective stages
			else if (activity.activity_type === "stage_entry" || activity.activity_type === "stage_exit") {
				const stage = activity.stage_name;
				if (stage && grouped[stage]) {
					grouped[stage].push(activity);
				} else if (stage) {
					grouped[stage] = [activity];
				}
			}
			// Handle other field updates - show in their stage
			else if (activity.activity_type === "field_update") {
				const stage = activity.stage_name;
				if (stage && grouped[stage]) {
					grouped[stage].push(activity);
				} else if (stage) {
					grouped[stage] = [activity];
				}
			}
			// Handle bulk field updates - show in their stage
			else if (activity.activity_type === "bulk_field_update") {
				const stage = activity.stage_name;
				if (stage && grouped[stage]) {
					grouped[stage].push(activity);
				} else if (stage) {
					grouped[stage] = [activity];
				}
			}
			// Default: place in stage_name if available
			else if (activity.stage_name && grouped[activity.stage_name]) {
				grouped[activity.stage_name].push(activity);
			} else if (activity.stage_name) {
				grouped[activity.stage_name] = [activity];
			}
		});

		return grouped;
	}, [activityData]);

	// Extract digitizer activities separately
	const digitizerActivities = useMemo(() => {
		if (!activityData || !activityData.activities) return [];
		
		return activityData.activities
			.filter((activity) => 
				activity.activity_type === "digitizer_entry" || 
				activity.activity_type === "digitizer_exit"
			)
			.slice()
			.sort((a, b) => {
				// Sort by timestamp (oldest first)
				const timeA = moment(a.timestamp).valueOf();
				const timeB = moment(b.timestamp).valueOf();
				return timeA - timeB;
			});
	}, [activityData]);

	// Timewise activities - all activities sorted by timestamp descending (latest first)
	const timewiseActivities = useMemo(() => {
		if (!activityData || !activityData.activities) return [];
		
		return activityData.activities
			.slice()
			.sort((a, b) => {
				// Sort by timestamp descending (latest first)
				const timeA = moment(a.timestamp).valueOf();
				const timeB = moment(b.timestamp).valueOf();
				return timeB - timeA;
			});
	}, [activityData]);

	const getActivityTypeColor = (activityType) => {
		switch (activityType) {
			case "created":
				return "green";
			case "stage_change":
				return "blue";
			case "stage_entry":
				return "cyan";
			case "stage_exit":
				return "orange";
			case "field_update":
				return "purple";
			case "bulk_field_update":
				return "purple";
			case "digitizer_entry":
				return "cyan";
			case "digitizer_exit":
				return "orange";
			default:
				return "default";
		}
	};

	// Check if activity represents a rejection (going back from embroidary)
	const isRejection = (activity) => {
		return activity.activity_type === "stage_change" && 
		       activity.previous_stage === STAGES.EMBROIDARY &&
		       (!activity.stage_name || activity.stage_name === "" || activity.stage_name === null);
	};

	const formatFieldName = (field) => {
		return field
			.split('_')
			.map(word => word.charAt(0).toUpperCase() + word.slice(1))
			.join(' ');
	};

	const toggleExpand = (activityId) => {
		setExpandedItems(prev => ({
			...prev,
			[activityId]: !prev[activityId]
		}));
	};

	const formatActivityDescription = (activity) => {
		switch (activity.activity_type) {
			case "created":
				return `JobCard created`;

			case "stage_change":
				// Check if this is a rejection from embroidary
				if (isRejection(activity)) {
					return "Given back to digitizer";
				}
				return activity.previous_stage && activity.stage_name
					? `Forwarded to "${getFormalName(activity.stage_name)}"`
					: activity.previous_stage
					? `Stage changed from "${getFormalName(activity.previous_stage)}"`
					: `Stage set to "${getFormalName(activity.stage_name)}"`;

			case "stage_entry":
				return `Entered this stage`;

			case "stage_exit":
				const duration = activity.duration_formatted || "N/A";
				return `Exited after ${duration}`;

			case "digitizer_entry":
				return `Entered digitizer dashboard`;

			case "digitizer_exit":
				const digitizerDuration = activity.duration_formatted || "N/A";
				return `Exited digitizer dashboard after ${digitizerDuration}`;

			case "field_update":
				return `Field "${activity.field_name}" updated`;

			case "bulk_field_update": {
				const changeCount = activity.changes ? activity.changes.length : 0;
				return `Fields Updated: ${changeCount} field(s) updated`;
			}

			default:
				return activity.notes || "Activity recorded";
		}
	};

	const renderActivityDetails = (activity) => {
		// Don't show details for is_degitizer_stage field updates
		if (activity.field_name === "is_degitizer_stage") {
			return null;
		}

		const details = [];

		// Handle individual field_update (non-bulk)
		if (activity.activity_type === "field_update") {
			if (activity.field_name) {
				details.push(
					<div key="field" style={{ marginTop: 6, fontSize: "12px" }}>
						<strong>Field:</strong> {activity.field_name}
					</div>
				);
			}

			if (activity.old_value !== null && activity.old_value !== undefined) {
				details.push(
					<div key="old" style={{ marginTop: 4, fontSize: "12px" }}>
						<strong>Old:</strong>{" "}
						<span style={{ color: "#ff4d4f" }}>{activity.old_value || "(empty)"}</span>
					</div>
				);
			}

			if (activity.new_value !== null && activity.new_value !== undefined) {
				details.push(
					<div key="new" style={{ marginTop: 4, fontSize: "12px" }}>
						<strong>New:</strong>{" "}
						<span style={{ color: "#52c41a" }}>{activity.new_value || "(empty)"}</span>
					</div>
				);
			}
		}

		if (activity.duration_formatted && (activity.activity_type === "stage_exit" || activity.activity_type === "digitizer_exit")) {
			details.push(
				<div key="duration" style={{ marginTop: 4, fontSize: "12px", color: "#faad14" }}>
					<strong>Time Spent:</strong> {activity.duration_formatted}
				</div>
			);
		}

		if (activity.activity_type === "digitizer_entry" && activity.stage_entry_time) {
			details.push(
				<div key="entry_time" style={{ marginTop: 4, fontSize: "12px" }}>
					<strong>Entry Time:</strong> {moment(activity.stage_entry_time).format("DD MMM YYYY, HH:mm")}
				</div>
			);
		}

		if (activity.activity_type === "digitizer_exit" && activity.stage_entry_time) {
			details.push(
				<div key="entry_time_for_exit" style={{ marginTop: 4, fontSize: "12px" }}>
					<strong>Entry Time:</strong> {moment(activity.stage_entry_time).format("DD MMM YYYY, HH:mm")}
				</div>
			);
		}

		if (activity.activity_type === "digitizer_exit") {
			details.push(
				<div key="exit_time" style={{ marginTop: 4, fontSize: "12px" }}>
					<strong>Exit Time:</strong> {moment(activity.timestamp).format("DD MMM YYYY, HH:mm")}
				</div>
			);
		}

		return details.length > 0 ? <div style={{ marginLeft: 16, marginTop: 4 }}>{details}</div> : null;
	};

	const renderActivityItem = (activity) => {
		// Special handling for bulk_field_update with collapsible UI
		if (activity.activity_type === "bulk_field_update" && activity.changes && activity.changes.length > 0) {
			const isExpanded = expandedItems[activity.id];
			const changeCount = activity.changes.length;
			const validChanges = activity.changes.filter(change => change.field !== "is_degitizer_stage");
			
			if (validChanges.length === 0) {
				return null; // Skip if no valid changes
			}

			return (
				<div
					key={activity.id}
					style={{
						padding: "8px 12px",
						marginBottom: 8,
						backgroundColor: "#fafafa",
						borderLeft: `3px solid #722ed1`,
						borderRadius: "4px",
					}}
				>
					<div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
						<div style={{ flex: 1 }}>
							<div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
								<Tag 
									color="purple" 
									style={{ fontSize: "11px" }}
								>
								 FIELD UPDATE
								</Tag>
								{activity.changed_by && (
									<span style={{ fontSize: "12px", color: "#666" }}>
										<UserOutlined style={{ marginRight: 4 }} />
										{activity.changed_by}
									</span>
								)}
							</div>
							<div style={{ fontSize: "13px", fontWeight: 500, marginBottom: 4 }}>
								{formatActivityDescription(activity)}
							</div>
							<div style={{ fontSize: "11px", color: "#999", marginBottom: 4 }}>
								{moment(activity.timestamp).format("DD MMM YYYY, HH:mm")}
							</div>
							{/* Down arrow to expand */}
							<button
								onClick={() => toggleExpand(activity.id)}
								style={{
									background: "none",
									border: "none",
									cursor: "pointer",
									padding: 0,
									marginTop: 4,
									color: "#666",
									display: "flex",
									alignItems: "center",
									gap: 4,
								}}
							>
								<DownOutlined 
									style={{ 
										fontSize: "12px",
										transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
										transition: "transform 0.2s"
									}} 
								/>
								<span style={{ fontSize: "11px" }}>View changes</span>
							</button>
						</div>
					</div>

					{/* Expanded Content */}
					{isExpanded && (
						<div style={{
							marginTop: 8,
							marginLeft: 16,
							padding: "8px",
							backgroundColor: "#fff",
							borderRadius: "4px",
						}}>
							<div style={{
								display: "grid",
								gridTemplateColumns: "repeat(5, 1fr)",
								gap: "5px",
							}}>
								{validChanges.map((change, idx) => (
									<div
										key={idx}
										style={{
											backgroundColor: "#fff",
											borderRadius: "4px",
											padding: "5px",
											border: "1px solid #e8e8e8",
											transition: "border-color 0.2s",
										}}
										onMouseEnter={(e) => e.currentTarget.style.borderColor = "#d9d9d9"}
										onMouseLeave={(e) => e.currentTarget.style.borderColor = "#e8e8e8"}
									>
										<div style={{ marginBottom: "4px" }}>
											<span style={{ fontSize: "9px", fontWeight: 600, color: "#262626" }}>
												{formatFieldName(change.field)}
											</span>
										</div>
										
										{/* Always show both old and new values - no gap between them */}
										<div style={{ display: "flex", flexDirection: "column", gap: "0px" }}>
											<div>
												<div style={{ fontSize: "7px", color: "#8c8c8c", marginBottom: "1px", fontWeight: 500 }}>
													Old
												</div>
												<div style={{
													padding: "3px 5px",
													backgroundColor: "#fff1f0",
													border: "1px solid #ffccc7",
													borderRadius: "2px",
													color: "#cf1322",
													fontSize: "9px",
													fontWeight: 500,
													wordBreak: "break-word",
													minHeight: "18px",
													display: "flex",
													alignItems: "center",
												}}>
													{(() => {
														// Support both 'old' and 'old_value' for backward compatibility
														const oldVal = change.old !== undefined ? change.old : (change.old_value !== undefined ? change.old_value : null);
														if (oldVal !== null && oldVal !== undefined && oldVal !== "") {
															return String(oldVal);
														} else {
															// Show "None" for all empty/null/undefined old values
															return <span style={{ fontStyle: "italic", color: "#999", fontSize: "9px" }}>None</span>;
														}
													})()}
												</div>
											</div>
											
											<div style={{ display: "flex", alignItems: "center", justifyContent: "center", color: "#8c8c8c", fontSize: "8px", margin: "1px 0" }}>
												↓
											</div>
											
											<div>
												<div style={{ fontSize: "7px", color: "#8c8c8c", marginBottom: "1px", marginTop: "0px", fontWeight: 500 }}>
													New
												</div>
												<div style={{
													padding: "3px 5px",
													backgroundColor: "#f6ffed",
													border: "1px solid #b7eb8f",
													borderRadius: "2px",
													color: "#389e0d",
													fontSize: "9px",
													fontWeight: 500,
													wordBreak: "break-word",
													minHeight: "18px",
													display: "flex",
													alignItems: "center",
												}}>
													{(() => {
														// Support both 'new' and 'new_value' for backward compatibility
														const newVal = change.new !== undefined ? change.new : (change.new_value !== undefined ? change.new_value : null);
														if (newVal !== null && newVal !== undefined && newVal !== "") {
															return String(newVal);
														} else if (newVal === "") {
															return <span style={{ fontStyle: "italic", color: "#999", fontSize: "8px" }}>(empty string)</span>;
														} else {
															return <span style={{ fontStyle: "italic", color: "#999", fontSize: "8px" }}>(empty)</span>;
														}
													})()}
												</div>
											</div>
										</div>
									</div>
								))}
							</div>
						</div>
					)}
				</div>
			);
		}

		// Regular activity items (non-bulk)
		return (
			<div
				key={activity.id}
				style={{
					padding: "8px 12px",
					marginBottom: 8,
					backgroundColor: "#fafafa",
					borderLeft: `3px solid ${
						isRejection(activity) ? "#ff4d4f" :
						activity.activity_type === "created" ? "#52c41a" :
						activity.activity_type === "stage_exit" || activity.activity_type === "digitizer_exit" ? "#faad14" :
						activity.activity_type === "stage_entry" || activity.activity_type === "digitizer_entry" ? "#1890ff" :
						activity.activity_type === "field_update" ? "#722ed1" : "#d9d9d9"
					}`,
					borderRadius: "4px",
				}}
			>
				<div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
					<div style={{ flex: 1 }}>
						<div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
							<Tag 
								color={isRejection(activity) ? "red" : getActivityTypeColor(activity.activity_type)} 
								style={{ fontSize: "11px" }}
							>
								{isRejection(activity) ? "JOB CARD REJECTED" : activity.activity_type.replace(/_/g, " ").toUpperCase()}
							</Tag>
							{activity.changed_by && (
								<span style={{ fontSize: "12px", color: "#666" }}>
									<UserOutlined style={{ marginRight: 4 }} />
									{activity.changed_by}
								</span>
							)}
						</div>
						<div style={{ fontSize: "13px", fontWeight: 500, marginBottom: 4 }}>
							{formatActivityDescription(activity)}
						</div>
						{/* Show creation details prominently for created activities */}
						{activity.activity_type === "created" && (
							<div style={{ 
								marginTop: 8, 
								padding: "8px 12px", 
								backgroundColor: "#f6ffed", 
								border: "1px solid #b7eb8f",
								borderRadius: "4px",
								marginBottom: 8
							}}>
								<div style={{ fontSize: "13px", color: "#52c41a", fontWeight: 500 }}>
									<strong>Created By:</strong> {activity.changed_by || "System"}
								</div>
								<div style={{ fontSize: "13px", color: "#52c41a", marginTop: 4 }}>
									<strong>Created At:</strong> {moment(activity.timestamp).format("DD MMM YYYY, HH:mm")}
								</div>
								{activity.stage_name && (
									<div style={{ fontSize: "13px", color: "#52c41a", marginTop: 4 }}>
										<strong>Initial Stage:</strong> {getFormalName(activity.stage_name)}
									</div>
								)}
							</div>
						)}
						{renderActivityDetails(activity)}
						<div style={{ fontSize: "11px", color: "#999", marginTop: 4 }}>
							{moment(activity.timestamp).format("DD MMM YYYY, HH:mm")}
						</div>
					</div>
				</div>
			</div>
		);
	};

	const handleCancel = () => {
		setExpandedItems({}); // Reset expanded items when modal closes
		onCancel();
	};

	return (
		<>
			<style>
				{`
					.activity-log-modal .ant-modal-content {
						border-radius: 12px !important;
						overflow: hidden;
						max-height: 100vh;
						display: flex;
						flex-direction: column;
					}
					.activity-log-modal .ant-modal-header {
						border-radius: 12px 12px 0 0 !important;
						flex-shrink: 0;
					}
					.activity-log-modal .ant-modal-body {
						border-radius: 0 0 12px 12px !important;
						overflow-y: auto;
						flex: 1;
						max-height: calc(100vh - 110px);
					}
				`}
			</style>
			<Modal
			title={`Activity Log - JobCard #${jobcardSerialNumber || activityData?.[0]?.jobcard?.serial_number || jobcardId}`}
			open={open}
			onCancel={handleCancel}
			footer={null}
			width={900}
			destroyOnClose
			rootClassName="activity-log-modal"
			centered
			styles={{
				content: { 
					borderRadius: "12px",
					overflow: "hidden",
					maxHeight: "90vh"
				},
				body: { 
					borderRadius: "12px",
					maxHeight: "calc(90vh - 110px)",
					overflowY: "auto",
					padding: "24px"
				}
			}}
		>
			{loading ? (
				<div style={{ textAlign: "center", padding: "40px 0" }}>
					<Spin size="large" />
				</div>
			) : !activityData || !activityData.activities || activityData.activities.length === 0 ? (
				<Empty description="No activity logs found for this JobCard" />
			) : (
				<div>
					<Descriptions bordered size="small" style={{ marginBottom: 20 }}>
						<Descriptions.Item label="Total Activities">
							{activityData.total_activities}
						</Descriptions.Item>
					</Descriptions>

					{/* View Type Selector */}
					<div style={{ marginBottom: 20 }}>
						<Select
							value={viewType}
							onChange={setViewType}
							style={{ width: 200 }}
							options={[
								{ label: "Stagewise", value: "stagewise" },
								{ label: "Timewise", value: "timewise" }
							]}
						/>
					</div>

					{/* Timewise View */}
					{viewType === "timewise" && (
						<div style={{ marginTop: 20 }}>
							{timewiseActivities.map((activity) => renderActivityItem(activity))}
						</div>
					)}

					{/* Stagewise View */}
					{viewType === "stagewise" && (
						<div style={{ marginTop: 20 }}>
						{STAGES_ORDER.map((stage, index) => {
							const activities = (activitiesByStage[stage] || []).slice().sort((a, b) => {
								// Stage exit should always be last
								if (a.activity_type === "stage_exit" && b.activity_type !== "stage_exit") {
									return 1; // a comes after b
								}
								if (a.activity_type !== "stage_exit" && b.activity_type === "stage_exit") {
									return -1; // a comes before b
								}
								// For all other activities, sort by timestamp (oldest first)
								const timeA = moment(a.timestamp).valueOf();
								const timeB = moment(b.timestamp).valueOf();
								return timeA - timeB;
							});
							const isLast = index === STAGES_ORDER.length - 1;

							return (
								<React.Fragment key={stage}>
									{/* Stage Name Header */}
									<div style={{ 
										padding: "12px 16px"
									}}>
										<h3 style={{ 
											margin: 0, 
											fontSize: "16px", 
											fontWeight: 600,
											color: "#1890ff"
										}}>
											{getFormalName(stage)}
										</h3>
									</div>

									{/* Activities for this stage */}
									{activities.length > 0 && (
										<div style={{ marginLeft: 12, marginBottom: 16 }}>
											{activities.map((activity) => renderActivityItem(activity))}
										</div>
									)}

									{/* Arrow pointing down (except for last stage) */}
									{!isLast && (
										<div style={{ 
											textAlign: "left", 
											paddingLeft: "16px"
										}}>
											<ArrowDownOutlined 
												style={{ 
													fontSize: "20px", 
													color: "#1890ff",
												}} 
											/>
										</div>
									)}
								</React.Fragment>
							);
						})}

							{/* Digitizer Activities Section */}
							{digitizerActivities.length > 0 && (
								<>
									<Divider style={{ margin: "30px 0 20px 0" }} />
									<div>
										{/* Digitizer Heading */}
										<div style={{ 
											padding: "12px 16px"
										}}>
											<h3 style={{ 
												margin: 0, 
												fontSize: "16px", 
												fontWeight: 600,
												color: "#1890ff"
											}}>
												Digitizer
											</h3>
										</div>

										{/* Digitizer Activities */}
										<div style={{ marginLeft: 12, marginBottom: 16 }}>
											{digitizerActivities.map((activity) => renderActivityItem(activity))}
										</div>
									</div>
								</>
							)}
						</div>
					)}
				</div>
			)}
		</Modal>
		</>
	);
};

export default ActivityLogModal;
